import { useCallback, useEffect, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { alterarRegra, listarRegras } from '../api/pontuacao';
import type { RegrasDaFase, ValorDaRegra } from '../api/pontuacao';

interface PontuacaoPainelProps {
    csrfToken: string;
    /** Só as regras desta fase (a aba da fase em Configurações); sem ela, todas. */
    fase?: string;
}

/**
 * Rótulos das fases do ranking. RN-AP-012: o Player 1 não pontua para o ranking e não tem regra
 * aqui, então a tela o esconde mesmo que um servidor antigo o mande; uma fase nova do servidor
 * continua aparecendo (com o próprio id como título, pelo fallback `FASES[fase] ?? fase`).
 */
const FASES: Record<string, string> = {
    scanpixel: 'Scan Pixel', labpixels: 'Laboratório de Pixels', versus: 'Modo Versus',
};

/** Rótulo e explicação de cada regra; a ordem aqui é a ordem na tela. */
const REGRAS: Record<string, { rotulo: string; ajuda?: string }> = {
    pontos_por_enigma: { rotulo: 'Pontos por enigma correto' },
    bonus_colocacao_1: { rotulo: 'Bônus do 1º lugar', ajuda: 'Só para o grupo que completa os cinco enigmas.' },
    bonus_colocacao_2: { rotulo: 'Bônus do 2º lugar' },
    bonus_colocacao_3: { rotulo: 'Bônus do 3º lugar' },
    destino: { rotulo: 'A pontuação vai para' },
    destino_bonus: { rotulo: 'A pergunta bônus vai para' },
};

const NOTA_DA_FASE: Record<string, string> = {
    labpixels: 'Os pontos de cada pergunta (inclusive a bônus) são editados na aba Variáveis.',
    versus: 'A pontuação é a soma das notas do facilitador (4 critérios, de 1 a 5, por case).',
};

function mensagemDoErro(erro: unknown): string {
    if (erro instanceof ErroRespostaHttp) {
        const turmas = erro.corpo?.turmas_com_fase_aberta;
        if (Array.isArray(turmas) && turmas.length) return `${String(erro.corpo?.mensagem)} Turmas: ${turmas.join(', ')}.`;
        if (typeof erro.corpo?.mensagem === 'string') return erro.corpo.mensagem;
        if (erro.status === 401 || erro.status === 403) return 'Sessão expirada ou sem permissão. Entre de novo.';
    }
    return 'Não foi possível salvar. Tente novamente.';
}

export function PontuacaoPainel({ csrfToken, fase: faseFixa }: PontuacaoPainelProps) {
    const [fases, setFases] = useState<RegrasDaFase[] | null>(null);
    /** O que foi digitado e ainda não salvo: `fase.chave` → texto do campo. */
    const [rascunho, setRascunho] = useState<Record<string, string>>({});
    const [erro, setErro] = useState<string | null>(null);
    const [aviso, setAviso] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);

    const carregar = useCallback(async () => {
        try {
            setFases(await listarRegras());
        } catch (falha) {
            setFases([]);
            setErro(mensagemDoErro(falha));
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    async function salvar(fase: string, chave: string, valor: ValorDaRegra) {
        setErro(null);
        setAviso(null);
        setOcupado(true);
        try {
            const atualizada = await alterarRegra(fase, chave, valor, csrfToken);
            setFases((atual) => (atual ?? []).map((item) => (item.fase === fase ? atualizada : item)));
            setRascunho(({ [`${fase}.${chave}`]: _salvo, ...resto }) => resto);
            setAviso(`${FASES[fase] ?? fase}: ${REGRAS[chave]?.rotulo ?? chave} salvo.`);
        } catch (falha) {
            setErro(mensagemDoErro(falha));
            await carregar();
        } finally {
            setOcupado(false);
        }
    }

    if (fases === null) return <p className="estado-variaveis">Carregando a pontuação…</p>;

    // Na aba da fase, só as regras dela; fase sem regra na tela (Player 1) não desenha nada.
    const doPainel = fases.filter(({ fase }) => fase !== 'player1' && (!faseFixa || fase === faseFixa));
    if (faseFixa && doPainel.length === 0) return null;

    return (
        <div className="cadastro">
            {erro && <p className="mensagem-login aviso-cadastro" role="alert">{erro}</p>}
            {aviso && <p className="mensagem-salvo" role="status">{aviso}</p>}

            {doPainel.map(({ fase, regras, editavel, turmas_com_fase_aberta: abertas }) => (
                <section className="bloco-cadastro" key={fase} aria-labelledby={`pontuacao-${fase}`}>
                    <h3 id={`pontuacao-${fase}`}>{FASES[fase] ?? fase}</h3>
                    {!editavel && (
                        <p className="aviso-fase-aberta">
                            Grupos jogando em {abertas.join(', ')}: a pontuação só muda quando o facilitador finalizar esses grupos.
                        </p>
                    )}
                    {NOTA_DA_FASE[fase] && <p className="nota-pontuacao">{NOTA_DA_FASE[fase]}</p>}
                    <div className="form-cadastro">
                        {Object.keys(REGRAS).filter((chave) => chave in regras).map((chave) => {
                            const id = `${fase}.${chave}`;
                            const atual = regras[chave];
                            if (typeof atual === 'string') {
                                return (
                                    <label key={chave}>
                                        {REGRAS[chave].rotulo}
                                        <select
                                            value={atual} disabled={!editavel || ocupado}
                                            onChange={(e) => void salvar(fase, chave, e.target.value as ValorDaRegra)}
                                        >
                                            <option value="grupo">Ranking de grupos</option>
                                            <option value="individual">Ranking individual</option>
                                        </select>
                                    </label>
                                );
                            }
                            const texto = rascunho[id] ?? String(atual);
                            const numero = Number(texto);
                            const valido = /^\d{1,4}$/.test(texto) && numero <= 1000;
                            return (
                                <div className="campo-pontos" key={chave}>
                                    <label>
                                        {REGRAS[chave].rotulo}
                                        <input
                                            type="text" inputMode="numeric" value={texto} disabled={!editavel || ocupado}
                                            aria-invalid={!valido} title={REGRAS[chave].ajuda}
                                            onChange={(e) => setRascunho({ ...rascunho, [id]: e.target.value })}
                                        />
                                    </label>
                                    <button
                                        className="botao-sair" type="button"
                                        disabled={!editavel || ocupado || !valido || numero === atual}
                                        aria-label={`Salvar ${REGRAS[chave].rotulo} de ${FASES[fase] ?? fase}`}
                                        onClick={() => void salvar(fase, chave, numero)}
                                    >
                                        Salvar
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </section>
            ))}
            <p className="nota-pontuacao">
                Mudar uma regra vale para as próximas apurações. Resultado já apurado não é recalculado.
            </p>
        </div>
    );
}
